import type * as Phaser from 'phaser';
import { getVisualAssetEntry, getVisualAssetManifest } from '../data/assets/visualAssets';
import { Logger, createLogger } from './Logger';

export interface TextureCacheLike {
  exists(key: string): boolean;
}

export interface VisualTextureResolveResult {
  readonly requestedKey: string;
  readonly resolvedKey: string;
  readonly source: 'direct' | 'alias' | 'fallback' | 'missing-fallback';
  readonly fallbackUsed: boolean;
}

export function usesFallbackTexture(result: Pick<VisualTextureResolveResult, 'source' | 'fallbackUsed'>): boolean {
  return result.fallbackUsed || result.source === 'fallback' || result.source === 'missing-fallback';
}

export class VisualTextureResolver {
  private readonly textureAliases = new Map<string, string>();
  private readonly logger: Logger;

  constructor(
    private readonly textures: TextureCacheLike,
    logger: Logger = createLogger('VisualTextureResolver')
  ) {
    this.logger = logger;
    for (const asset of getVisualAssetManifest(this.logger)) {
      for (const alias of asset.aliases ?? []) {
        this.textureAliases.set(alias, asset.key);
      }
    }
  }

  public resolve(requestedKey: string, fallbackKey: string = 'enemy'): VisualTextureResolveResult {
    if (this.textures.exists(requestedKey)) {
      this.logger.debug('resolve:direct', { requestedKey, resolvedKey: requestedKey, source: 'direct' });
      return { requestedKey, resolvedKey: requestedKey, source: 'direct', fallbackUsed: false };
    }

    const aliasKey = this.textureAliases.get(requestedKey) ?? getVisualAssetEntry(requestedKey)?.key;
    if (aliasKey && this.textures.exists(aliasKey)) {
      this.logger.debug('resolve:alias', { requestedKey, resolvedKey: aliasKey, source: 'alias' });
      return { requestedKey, resolvedKey: aliasKey, source: 'alias', fallbackUsed: requestedKey !== aliasKey };
    }

    const manifestFallback = getVisualAssetEntry(requestedKey)?.fallbackKey;
    const fallbackCandidates = [manifestFallback, fallbackKey, 'enemy'].filter((key): key is string => Boolean(key));
    const resolvedFallback = fallbackCandidates.find(key => this.textures.exists(key)) ?? fallbackCandidates[0] ?? requestedKey;

    if (this.textures.exists(resolvedFallback)) {
      this.logger.warn('resolve:fallback', { requestedKey, resolvedKey: resolvedFallback, source: 'fallback' });
      return { requestedKey, resolvedKey: resolvedFallback, source: 'fallback', fallbackUsed: true };
    }

    this.logger.error('resolve:missing-fallback', { requestedKey, fallbackCandidates });
    return { requestedKey, resolvedKey: resolvedFallback, source: 'missing-fallback', fallbackUsed: true };
  }
}

export function createVisualTextureResolver(
  scene: Pick<Phaser.Scene, 'textures'>,
  logger: Logger = createLogger('VisualTextureResolver')
): VisualTextureResolver {
  return new VisualTextureResolver(scene.textures, logger);
}
